/**
 * @module app.core
 */

import { autoinject } from 'aurelia-framework';
import { Subscription } from 'aurelia-event-aggregator';
import { MsalService } from 'app/shared/services/msal-service';
import { SignOutEvent } from 'app/core/lib/signon-events';
import { SignOutBase } from 'app/core/pages/signon/signout-base';

@autoinject()
export class SignoutViewModel extends SignOutBase {

    private subscriptions: Array<Subscription> = [];

    constructor(
        protected readonly msalService: MsalService,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected attached(): void {
        this.subscriptions.push(
            this.events.subscribe(SignOutEvent, (e: SignOutEvent) => this.handleSignOutEvent(e))
        );

        this.localLogOut();

        this.signOut();
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    /**
     * Initiate the external Sign Out process for a given Sign On Provider
     */
    protected async signOut(): Promise<void> {
        if(!this.signOnProvider) { return; }

        try {
            this.busyState.beginProcessing();

            this.statusMessage = this.i18n.tr('core:local.signout.init');

            // TODO[MW]: Move custom sign out methods to their own views.  This page should just be an orchestrator to kick off the appropriate sign out method depending on the provider
            switch(this.signOnProvider.method) {
                case 'oidc-msal':
                    if(!this.msalService.isInitialized) {
                        await this.msalService.init(this.signOnProvider.key);
                    }
                    await this.msalService.logOut();
                    break;
                default:
                    throw new Error("Unsupported Sign On provider");
            }
        }
        catch(error) {
            this.logger.error(error);
            this.errorMessage = this.i18n.tr('core:local.signout.error');
            this.signingOut = false;
        }
        finally {
            this.busyState.endProcessing();
        }
    }

    protected async handleSignOutEvent(event: SignOutEvent) {
        if(this.signingOut && this.signOnProvider) {
            if(event.isSuccess) {
                this.statusMessage = this.i18n.tr('core:messages.logout.userLoggedOut');
                if(event.hasLogoutRedirect) {
                    // Redirecting the user to a post-logout redirect URL.  Supress the "Return" button.
                    this.statusMessage += '  ' + this.i18n.tr('core:local.signout.redirecting');
                }
                else {
                    this.signingOut = false;
                }
            }
            else {
                this.signingOut = false;
                this.errorMessage = this.i18n.tr('core:local.signout.error');
            }
        }
    }

}
